'use strict'

Vue.component('awe-carousel', {
  props: {
    products: {type: Array, value: []},
    defaultEan: {type: String, default: ''},
    idType: {type: String}, // type of product id used to identify the selected product
    rtl: {type: Boolean, value: false},
    // scrolling behavior - desktop only
    scrollMode: {type: String, default: 'hover', validator: (val) => {return ['hover', 'longClick', 'click'].includes(val)}},
    speed: {type: Number, default: 560}, // scroll speed in pixels per second
    fit: {type: Boolean, default: false}, // whether to adjust item width to fit exactly on the carousel width
    pagination: {type: Boolean, default: false}, // whether to request new products at end of carousel
  },
  data: function(){
    return {
      scrollOffset: 0, // translation amount, in pixels.
      selectedIdx: -1,
      eventTypeSelection: false, // whether we've had a mouseenter/touchstart event and unregistered the other type
      minpos: Number, // minimum value for content translation (value at scroll end course)
      swipeStartX: -1,
      updateTimeout: -1,
      rafid: 0, // requestAnimationFrame id
      _baseItemWidth: 0, // width of the first item before the first adjustment.
      _fitTimeoutId: 0, // timeout id for throttling item width adjustment
      paginationObserver: null, // intersection observer for pagination detection

      // IntersectionObserver settings
      PAGINATION_ROOT_MARGIN:'200px',
      PAGINATION_THRESHOLD: 0.1,
      i18n: window.i18n,
    }
  },
  computed: {
    computedStyle: function(){
      return {
        '-webkit-transform': `translateX(${this.scrollOffset}px)`,
        transform: `translateX(${this.scrollOffset}px)`
      }
    }
  },
  watch: {
    defaultEan: function(){ this.scrollToDefault('defaultEan') },
    products: function(){ 
      this.scrollToDefault('products')

      // on product update in pagination mode, wait for render and observe the new last product node.
      if (this.pagination && this.paginationObserver){
        this.paginationObserver.disconnect()

        this.$nextTick().then(() => {
          const lastProductNd = this.$el.querySelector('.content .pdt:last-child')
          if (lastProductNd)
            this.paginationObserver.observe(lastProductNd)
        })
      }
    }
  },
  created: function(){
  },
  mounted: function(){
    // bind events to left/right buttons
    const btns = this.$el.getElementsByClassName('scroll-btn')
    for (let i = 0; i < btns.length; i++){
      if (this.scrollMode === 'hover'){
        btns[i].addEventListener('mouseenter', this.startScroll, {passive: true})
        btns[i].addEventListener('mouseleave', this.endScroll, {passive: true})
      }
      else if (this.scrollMode === 'longClick'){
        btns[i].addEventListener('mousedown', this.startScroll, {passive: true})
        btns[i].addEventListener('mouseup', this.endScroll, {passive: true})
      }
      else if (this.scrollMode === 'click'){
        btns[i].addEventListener('click', this.startScroll, {passive: true})
      }

      // in click mode, the touchstart/end events are in conflicts, do not bind them.
      if (this.scrollMode !== 'click'){
        btns[i].addEventListener('touchstart', this.startScroll, {passive: true})
        btns[i].addEventListener('touchend', this.endScroll, {passive: true})
      }
    }
    const content = this.$el.getElementsByClassName('content')[0]
    content.addEventListener('touchstart', this.swipeStart, {passive: true})
    content.addEventListener('touchmove', this.swipe, {passive: true})

    if (this.fit)
      this.startObservers()

    if (this.products){
      if (this.defaultEan)
        this.selectedIdx = Math.max(0, this.products.findIndex(pdt => Utils.isDefaultProduct(pdt, this.defaultEan, this.idType)))
      else
        this.selectedIdx = 0
    }

    // APEX-199: in pagination mode, set up intersection observer to check when we reach the end of the carousel,
    // and request a new batch of products.
    if (this.pagination && window.IntersectionObserver && !this.paginationObserver){
      this.paginationObserver = new IntersectionObserver((entries, observer) => {
        if (entries[0].intersectionRatio >= observer.thresholds[0]){
          this.$emit('pagination-request')
          observer.disconnect()
        }
      }, {
        root: this.$el.getElementsByClassName('scroll-ctnr')[0],
        // includes this margin in root intersection area.
        // callback is triggered when observed element is ~200px (minus threshold) away from the end.
        rootMargin: this.PAGINATION_ROOT_MARGIN,
        threshold: this.PAGINATION_THRESHOLD,
      })
    }
  },

  // checks whether content overflows the container. If not, hide scroll buttons.
  updated: function(){
    if (this.updateTimeout != -1) return

    this.updateTimeout = setTimeout(async () => {
      await this.$nextTick()

      const ctnr = this.$el.getElementsByClassName('scroll-ctnr')[0],
            content = this.$el.getElementsByClassName('content')[0],
            ctnrWidth = ctnr && ctnr.getBoundingClientRect().width,
            contentWidth = content && content.getBoundingClientRect().width,
            hasOverflow = contentWidth > ctnrWidth,
            btns = this.$el.getElementsByClassName('scroll-btn')

      for (let i = 0; i < btns.length; i++){
        if (hasOverflow)
          btns[i].classList.remove('hidden')
        else
          btns[i].classList.add('hidden')
      }

      this.updateTimeout = -1
    }, 50)
  },
  methods: {
    // scrolls container to make the default product visible
    // propName (string) name of the property that was updated and triggered the method
    scrollToDefault: async function(propName){
      this.selectedIdx = Math.max(0, this.products.findIndex(pdt => Utils.isDefaultProduct(pdt, this.defaultEan, this.idType)))
      const nd = this.$el.querySelector(`.pdt:nth-child(${this.selectedIdx+1})`)

      if (!nd) return

      // on updated products, it's necessary to wait for dom rendering, otherwise minPos,
      // DOM rectangles sizes & boundaries are incorrect.
      if (propName === 'products')
        await this.$nextTick()

      const contentNd = this.$el.getElementsByClassName('content')[0],
            pdtPos = nd.getBoundingClientRect(),
            contentPos = contentNd.getBoundingClientRect()

      if (contentPos.width > contentPos.height){
        this.updateMinPos(contentNd)
        this.scrollOffset += contentPos.x - pdtPos.x
        this.respectBoundaries()
        if (this.scrollOffset < 0)
          this.$el.querySelector('.scroll-btn.left').classList.remove('disabled')
        else
          this.$el.querySelector('.scroll-btn.left').classList.add('disabled')

        if (this.scrollOffset === this.minPos)
          this.$el.querySelector('.scroll-btn.right').classList.add('disabled')
        else
          this.$el.querySelector('.scroll-btn.right').classList.remove('disabled')
      }
      else{
        nd.scrollIntoView({behavior: 'auto', block: 'center'})
      }
    },

    startScroll: function(e){
      if (e.currentTarget.classList.contains('disabled'))
        return

      if (!this.eventTypeSelection){
        // unregisters event handlers when another type of event occurs.
        // if we see a mouse(enter|down), we can remove the touchstart/touchend handlers.
        // Reciprocally, we can remove mouse(enter|leave|down|up) handlers when we see a touchstart event.
        let elems = this.$el.getElementsByClassName('scroll-btn'),
            events
        if (e.type === 'touchstart'){
          if (this.scrollMode === 'hover')
            this.unregisterEvents(elems, 'mouseenter', 'mouseleave')
          else if (this.scrollMode === 'longClick')
            this.unregisterEvents(elems, 'mousedown', 'mouseup')
        }
        else if (e.type === 'mouseenter' || e.type === 'mousedown')
          this.unregisterEvents(elems, 'touchstart', 'touchend')
        this.eventTypeSelection = true
      }

      var target = e.currentTarget,
          speed = target.classList.contains('right') ? -this.speed : this.speed

      // remove disabled class on oppposite button
      var disabledBtn = this.$el.querySelector('.scroll-btn.disabled')
      if (disabledBtn)
        disabledBtn.classList.remove('disabled')

      target.classList.add('hover')

      const content = this.$el.getElementsByClassName('content')[0]
      this.updateMinPos(content)

      // in click scrollMode, get the length by which we must scroll
      let itemWidth
      if (this.scrollMode === 'click'){
        itemWidth = content.children[0].getBoundingClientRect().width
        const styles = window.getComputedStyle(content.children[0]),
              margins = (parseFloat(styles.marginLeft) || 0) + (parseFloat(styles.marginRight) || 0)
        itemWidth += margins
      }
      // console.debug('item width: ' + itemWidth)

      // observe last product element for pagination detection
      if (this.paginationObserver){
        this.paginationObserver.observe(this.$el.querySelector('.pdt:last-child'))
      }

      const startOffset = this.scrollOffset // offset before animation
      let targetOffsetReached = false // whether we scrolled enough (click mode only)
      let start // start ts

      // requestAnimationFrame callback
      const cb = (ts) => {
        if (!start){
          start = ts
          this.rafid = window.requestAnimationFrame(cb)
          return
        }

        // computes scroll offset from beginning of animation
        const elapsed = ts - start
        let offset = (elapsed * speed) / 1000

        // click scrollMode: check if we scrolled the requested length
        if (this.scrollMode === 'click' && Math.abs(offset) > itemWidth){
          offset = offset > 0 ? itemWidth : -itemWidth
          targetOffsetReached = true
        }

        // console.debug('offset: ' + offset)

        this.scrollOffset = startOffset + offset
        this.respectBoundaries()

        // cancel animation & update button state if scrolled to the max
        if (elapsed > 0 && (this.scrollOffset === 0 || this.scrollOffset === this.minPos)){
          window.cancelAnimationFrame(this.rafid)
          target.classList.add('disabled')
          target.classList.remove('hover')
        }
        // in click scrollMode, check if target offset is reached
        else if (targetOffsetReached){
          window.cancelAnimationFrame(this.rafid)
        }
        else
          this.rafid = window.requestAnimationFrame(cb)
      }

      this.rafid = window.requestAnimationFrame(cb)
    },

    // when mouse leaves scroll zone or touchend/mouseup event
    endScroll: function(e){
      window.cancelAnimationFrame(this.rafid)
      e.currentTarget.classList.remove('hover')
    },

    // stores X coordinate of scroll content's touch, for later touchmove events
    swipeStart: function(e){
      this.swipeStartX = e.touches[0].screenX
      this.updateMinPos(e.currentTarget)

      // observe last product element for pagination detection
      if (this.paginationObserver){
        this.paginationObserver.observe(this.$el.querySelector('.pdt:last-child'))
      }
    },

    // Triggered on touchmove on content element
    // computes movement & updates offset
    // also does some UI gimmicks (toggles disabled/hover classes)
    swipe: function(e){
      // computes horizontal offset by which touch has moved
      const curX = e.touches[0].screenX,
          offset = this._toInt(curX - this.swipeStartX)

      this.swipeStartX = curX

      // console.log(`offset ${offset} / scrollOffset ${this.scrollOffset}`)

      // get button in scroll direction, and opposite button
      var target = this.$el.querySelector('.scroll-btn.left'),
          opposite = this.$el.querySelector('.scroll-btn.right')

      // if swipe in the opposite direction, just switch variables
      // offset > 0: swiping left
      // offset < 0: swiping right
      if (offset < 0)
        [target, opposite] = [opposite, target]

      if (target.classList.contains('disabled'))
        return

      // remove disabled class on oppposite button
      var disabledBtn = this.$el.querySelector('.scroll-btn.disabled')
      if (disabledBtn)
        disabledBtn.classList.remove('disabled')

      target.classList.add('hover') // applies hover style
      opposite.classList.remove('hover')

      this.scrollOffset += offset
      this.respectBoundaries()

      setTimeout(() => {
        // We need to wait the animation ending to know if we can hide or not the button
        // disables button and remove hover style when at scroll start/end
        if (this.scrollOffset === 0 && offset > 0 || this.scrollOffset === this.minPos && offset < 0){
          target.classList.add('disabled')
          target.classList.remove('hover')
        }
      }, 100)
    },

    // ensures the newly computed offset stays within min/max values
    respectBoundaries: function(){
      if (this.rtl){
        this.scrollOffset = Math.max(this.scrollOffset, 0)
        this.scrollOffset = Math.min(this.scrollOffset, this.minPos)
      }
      else{
        this.scrollOffset = Math.min(this.scrollOffset, 0)
        this.scrollOffset = Math.max(this.scrollOffset, this.minPos)
      }
    },

    // determines the lowest value for scrollOffset, i.e. the value for which the carousel is moved farthest.
    // Bound by Math.min/max to handle cases when content is smaller than container and does not need to scroll.
    updateMinPos: function(content){
      var scrollCtnr = this.$el.children[1]
      if (this.rtl)
        this.minPos = Math.max(0, content.scrollWidth - scrollCtnr.offsetWidth)
      else
        this.minPos = Math.min(0, scrollCtnr.offsetWidth - content.scrollWidth)
    },

    // Removes event handlers for the given elements
    unregisterEvents: function(elements, startEvt, endEvt){
      for (let i = 0; i < elements.length; i++){
        elements[i].removeEventListener(startEvt, this.startScroll)
        elements[i].removeEventListener(endEvt, this.endScroll)
      }
    },

    // rounds an integer
    _toInt: (value) => {
      if (value > 0)
        return Math.ceil(value)
      else if (value < 0)
        return Math.floor(value)
      return value
    },

    // adjusts carousel items' width to fit correctly
    resizeItems: function(){
      const ITEM_WIDTH_THRESHOLD = .8, // proportion of the last visible item that fits in page needed to fit it completely
            ctnr = this.$el.getElementsByClassName('scroll-ctnr')[0],
            content = this.$el.getElementsByClassName('content')[0],
            ctnrWidth = ctnr.getBoundingClientRect().width,
            contentWidth = content.getBoundingClientRect().width

      // no action needed if item list does not overflow
      if (contentWidth <= ctnrWidth)
        return

      // on first run, save base item width. Serves as reference for future runs to compute how many items should fit on page.
      if (!this._baseItemWidth)
        this._baseItemWidth = content.children[0].getBoundingClientRect().width

      // compute new width to fit correctly. Takes margins into account
      const styles = window.getComputedStyle(content.children[0]),
            margins = (parseFloat(styles.marginLeft) || 0) + (parseFloat(styles.marginRight) || 0)

      let pageSize = ctnrWidth / (this._baseItemWidth + margins)

      // we accept reducing item width if the last item is close to fitting
      // e.g. if page size is 5.9, fit 6 items instead of 5.
      pageSize = pageSize % 1 >= ITEM_WIDTH_THRESHOLD ? Math.ceil(pageSize) : Math.floor(pageSize)

      const newWidth = ctnrWidth / pageSize - margins

      console.debug(`cntr: ${ctnrWidth} / width: ${this._baseItemWidth}🠖${newWidth}, margins: ${margins}`)

      // apply new with to all items
      for (let i = 0; i < content.childElementCount; i++){
        const nd = content.children[i]
        nd.style.width = newWidth.toFixed(2) + 'px'
        nd.style['min-width'] = 'auto'
        nd.style['max-width'] = 'auto'
      }
    },

    // sets up Mutation and Resize observers for item width adjustment
    startObservers: function(){
      if (!window.MutationObserver)
        return

      const observerCallback = (mutList, obs) => {
        if (!this._fitTimeoutId){
          this._fitTimeoutId = setTimeout(() => {
            this.resizeItems()
            this._fitTimeoutId = 0
          }, 250)
        }
      }

      const mutObserver = new MutationObserver(observerCallback),
            content = this.$el.getElementsByClassName('content')[0]
      mutObserver.observe(content, {childList: true})

      if (window.ResizeObserver){
        const resizeObs = new ResizeObserver(observerCallback)
        resizeObs.observe(this.$el.getElementsByClassName('scroll-ctnr')[0])
      }

    },

    selectPdt: function(e){
      // handler is on the content node, to have a single listener instead of one on each product element.
      // So we need to go through the DOM tree to find which product was clicked.
      e.stopPropagation()
      let nd = null,
          target  = e.target
      while (nd == null && !target.classList.contains('scroll-ctnr') ){
        if (target.classList.contains('pdt'))
          nd = target
        else
          target = target.parentElement
      }
      if (nd){
        this.selectedIdx = parseInt(nd.dataset.idx, 10)
        this.$emit('product-select', this.products[this.selectedIdx])
      }
    }
  },

  template: `
    <div class="carousel">
      <div name="left-btn" class="scroll-btn left" v-bind:class="{disabled: !rtl, rtl: rtl}" tabindex="0">
      </div>

      <div class="scroll-ctnr">
        <div class="content" role="listbox" :aria-label="i18n.getString('CAROUSEL_ARIA_LABEL')" :style="computedStyle" v-on:click="selectPdt" v-on:keypress.enter="selectPdt">
          <div v-for="(pdt, i) in products" class="pdt" 
            v-bind:class="{selected: i === selectedIdx}"
            :data-ean="pdt.ean" 
            :data-idx="i" 
            role="option" 
            :aria-selected="i === selectedIdx ? 'true' : 'false'"
            tabindex="0"
           >
            <slot v-bind:pdt="pdt" v-bind:i="i">
            </slot>
          </div>
        </div>
      </div>

      <div name="right-btn" class="scroll-btn right" v-bind:class="{disabled: rtl, rtl: rtl}" tabindex="0">
      </div>
    </div>
  `
})
